import cn from 'classnames'
import {FC, FocusEvent, useState} from 'react'

import {Button} from '@/components/common/content/Button'
import {Text, TextVariant} from '@/components/common/content/Text'
import {getBlogContent} from '@/content/blog/blog'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {Industry} from '@/utils/contentful'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {ChevronIcon} from '../../common/icons/ChevronIcon'
import {IndustryCard} from './IndustryCard'

type IndustryListProps = {
  industries: Industry[]
}

export const IndustryList: FC<React.PropsWithChildren<IndustryListProps>> = ({industries}) => {
  const {t} = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const {title, buttonShowMore, buttonShowLess} = getBlogContent().industries

  // The collapsed list clips the pills below its max-height, but they stay in the tab order, so
  // reveal them when keyboard focus lands on one
  const expandIfFocusHidden = (e: FocusEvent<HTMLUListElement>) => {
    if (e.target.getBoundingClientRect().bottom > e.currentTarget.getBoundingClientRect().bottom) {
      setIsExpanded(true)
    }
  }

  return (
    <>
      <Text variant={TextVariant.h2} align="center">
        {t(title)}
      </Text>
      <ul className={cn('list', isExpanded && 'expanded')} onFocus={expandIfFocusHidden}>
        {industries.map((industry) => (
          <IndustryCard key={industry.name} industry={industry} />
        ))}
      </ul>
      <div className="button-wrapper">
        <Button
          icon={<ChevronIcon color={theme.palette.text.primary} isActive={isExpanded} />}
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? t(buttonShowLess) : t(buttonShowMore)}
        </Button>
      </div>

      <style jsx>{`
        .list {
          display: flex;
          flex-wrap: wrap;
          max-height: 135px;
          overflow: hidden;
          margin: ${theme.spacing[7.5]} -${theme.spacing[2]} 0;
        }
        .list.expanded {
          max-height: 100%;
          overflow: visible;
        }
        .button-wrapper {
          text-align: center;
          margin-top: ${theme.spacing[6.5]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .list {
            max-height: 370px;
            justify-content: center;
            margin-top: ${theme.spacing[4]};
          }
          .button-wrapper {
            margin-top: ${theme.spacing[4]};
          }
        }
      `}</style>
    </>
  )
}
